import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ChatMessage } from '@/types/chat';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import clsx from 'clsx';

// Placeholder icons (replace with lucide-react or similar if needed)
// For now, we'll keep them as simple spans or use actual characters
const MessageCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>; // Example SVG for chat icon
const SendIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>; // Example SVG for send icon
const XIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; // Example SVG for close icon
// Removed HelpCircleIcon

const N8N_WEBHOOK_URL = 'https://automate.solerro.com/webhook/ai-chat';

export const AiChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const greetingShownRef = useRef(false);
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && messages.length === 0 && !greetingShownRef.current) {
      setMessages([
        {
          id: 'initial-greeting',
          sender: 'ai',
          text: 'Hello! How can I assist you today?',
          timestamp: new Date(),
        },
      ]);
      greetingShownRef.current = true;
    }
    
    // Reset greeting flag when chat is closed
    if (!isOpen) {
      greetingShownRef.current = false;
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    if (isOpen && messages.length > 0) {
      // Try multiple approaches to ensure scrolling works
      // 1. Using the messagesEndRef
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      
      // 2. Using the ScrollArea viewport
      const scrollViewport = document.querySelector('.chat-scroll-viewport');
      if (scrollViewport) {
        setTimeout(() => {
          scrollViewport.scrollTop = scrollViewport.scrollHeight;
        }, 100); // Small delay to ensure content is rendered
      }
    }
  }, [messages, isOpen]);

  const toggleChat = () => setIsOpen(!isOpen);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() === '' || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const payload = {
        message: userMessage.text,
        userId: user?.id || 'anonymous',
        userName: user?.email || 'Anonymous User'
      };
      
      const response = await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing response as JSON:', parseError);
        throw new Error('Invalid JSON response from webhook');
      }
      
      const aiText = data.reply || data.message || data.response || data.output || 
                    (typeof data === 'string' ? data : 'Sorry, I could not process that.');
      
      const aiMessage: ChatMessage = {
        id: `ai-${Date.now()}`,
        sender: 'ai',
        text: aiText,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI chat error:', error);
      
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        sender: 'ai',
        text: 'Sorry, I couldn\'t connect to the AI assistant. The service might be temporarily unavailable. Please try again in a few moments.',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={toggleChat}
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 lg:bottom-8 lg:right-8 z-40 rounded-full w-14 h-14 p-0 shadow-lg bg-primary hover:bg-primaryHover text-white flex items-center justify-center transition-transform duration-300 ease-in-out hover:scale-105"
        aria-label="Open chat"
      >
        <MessageCircleIcon />
      </Button>
    );
  }

  return (
    <>
      <Card className={clsx(
        "fixed z-40 shadow-xl flex flex-col bg-white dark:bg-zinc-900 transition-all duration-300 ease-in-out border-0 dark:border dark:border-zinc-800",
        {
          'opacity-100 visible scale-100': isOpen,
          'opacity-0 invisible scale-95': !isOpen,
        },
        // Mobile (default): full screen
        "inset-0 rounded-none",
        // Tablet (md and up): specific dimensions, position, and rounded corners. Max height is viewport-aware.
        "md:inset-auto md:bottom-16 md:right-4 md:w-[380px] md:max-h-[calc(100vh-8rem)] md:h-auto md:rounded-lg md:border md:border-border dark:md:border-border",
        // Desktop (lg and up): Taller, wider, and slightly different position. Fixed height.
        "lg:bottom-20 lg:right-8 lg:w-[420px] lg:max-h-[650px] lg:h-[650px]"
      )}>
        <CardHeader className="px-4 py-2 border-b border-border dark:border-border bg-white dark:bg-muted flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg font-medium text-foreground dark:text-muted-foreground">
              {currentTenant?.company_name || 'Solerro'} AI Assistant
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="text-xs font-medium text-primary border-primary/30 hover:bg-primary/10 hover:text-primary hover:border-primary/50"
              onClick={() => navigate('/app/support-tickets')}
            >
              Tickets
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleChat} className="text-muted-foreground0 hover:text-foreground dark:text-muted-foreground dark:hover:text-muted-foreground" aria-label="Close chat">
              <XIcon />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-grow p-0 overflow-hidden bg-white dark:bg-muted border-t-0 border-b-0">
          <ScrollArea className="h-full chat-scroll-viewport -mt-[2px] -mb-[2px]" ref={scrollAreaRef}>
            <div className="flex flex-col px-4 py-[2px]" style={{ gap: '0.75rem' }}>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={clsx(
                    'flex items-start gap-3',
                    msg.sender === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  {msg.sender === 'ai' && (
                    <Avatar className="w-8 h-8 flex-shrink-0">
                      <AvatarImage src="/ava.png" alt="AI Assistant" />
                      <AvatarFallback>AI</AvatarFallback>
                    </Avatar>
                  )}
                  <div
                    className={clsx(
                      'max-w-[80%] px-4 py-3 rounded-lg shadow-sm',
                      msg.sender === 'user'
                        ? 'bg-primary/10 border border-primary/20 text-foreground dark:text-zinc-100 rounded-br-none'
                        : 'bg-muted dark:bg-muted border border-border dark:border-border text-foreground dark:text-zinc-100 rounded-bl-none'
                    )}
                  >
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                    <p className="text-xs text-muted-foreground0 dark:text-muted-foreground mt-1 text-right">
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  {msg.sender === 'user' && (
                    <Avatar className="h-8 w-8 flex-shrink-0 border border-border dark:border-border">
                    {user?.user_metadata?.avatar_url ? (
                      <AvatarImage src={user.user_metadata.avatar_url} alt={user.email || 'User'} />
                    ) : (
                      <AvatarFallback>{user?.email?.[0]?.toUpperCase() || 'U'}</AvatarFallback>
                    )}
                  </Avatar>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start items-start gap-3">
                    <Avatar className="h-8 w-8 flex-shrink-0 border border-border dark:border-border">
                      <AvatarImage src="/ava.png" alt="AI Assistant" />
                      <AvatarFallback>AI</AvatarFallback>
                    </Avatar>
                    <div className="bg-muted dark:bg-muted border border-border dark:border-border px-4 py-3 rounded-lg shadow-sm rounded-bl-none max-w-[80%]">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
        </CardContent>
        <CardFooter className="px-4 py-2 border-t border-border dark:border-border bg-white dark:bg-muted">
          <div className="flex w-full space-x-2">
            <Input
              type="text"
              placeholder="Type your message..."
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSendMessage()}
              disabled={isLoading}
              className="flex-grow px-4 py-2 rounded-lg border border-border dark:border-border bg-white dark:bg-muted text-foreground dark:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <Button 
              onClick={handleSendMessage} 
              disabled={isLoading || inputValue.trim() === ''} 
              className="bg-primary hover:bg-primaryHover text-white transition-colors"
              aria-label="Send message"
            >
              <SendIcon />
            </Button>
          </div>
        </CardFooter>
      </Card>
      <Button
        onClick={toggleChat}
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 lg:bottom-8 lg:right-8 z-40 rounded-full w-14 h-14 p-0 shadow-lg bg-primary hover:bg-primaryHover text-white flex items-center justify-center transition-transform duration-300 ease-in-out hover:scale-105"
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? <XIcon /> : <MessageCircleIcon />}
      </Button>
    </>
  );
};
